import { Box, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPagination, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import React, { FC, useState } from "react";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import moment from "moment";

import "./deviceCameraSettings.css";
import { PAGINATION_CONSTANTS } from "../../../utils/constants";
import { useGetDevicesListQuery } from "../../../redux/reducers/settings.reducer";
import TablePagination from "../../../components/TablePagination";
import { IDeviceDetails, IGetDevicesListRequestData } from "../../../types";
import ConnectedCamerasDialog from "./ConnectedCamerasDialog";

export interface IDeviceRowDetails {
	id: number;
	status: "Online" | "Offline";
	name: string;
	serial_number: string;
	system_version: string;
	local_ip: string;
	global_ip: string;
	image_quota: IDeviceDetails["image_quota"] | null;
}

type ImageQuotaSeverity = "success" | "warning" | "error";

function getImageQuotaSeverity(usagePercentage: number, isExceeded: boolean): ImageQuotaSeverity {
	if (isExceeded || usagePercentage >= 100) return "error";
	if (usagePercentage >= 75) return "warning";
	return "success";
}

function formatQuotaDate(value: string | null | undefined, inputFormat?: string) {
	if (!value) return "—";
	const parsed = inputFormat ? moment(value, inputFormat) : moment(value);
	return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : value;
}

function formatQuotaRange(startValue: string, endValue: string) {
	const startDate = moment(startValue);
	const endDate = moment(endValue);
	if (!startDate.isValid() || !endDate.isValid()) return "—";

	if (startDate.year() === endDate.year()) {
		if (startDate.month() === endDate.month()) {
			return `${startDate.format("DD")} ${startDate.format("MMM")} - ${endDate.format("DD MMM YYYY")}`;
		}
		return `${startDate.format("DD MMM")} - ${endDate.format("DD MMM YYYY")}`;
	}

	return `${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`;
}

const DevicesListPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ spacer: "grid-pagination-spacer-custom" }}
	/>
);

const DeviceCameraSettings: FC = () => {
	const [filters, setFilters] = useState<IGetDevicesListRequestData>({ page: 1 });
	const [selectedDeviceDetails, setSelectedCameraDetails] = useState<IDeviceRowDetails | null>(null);

	// APIS
	const {
		data: getDevicesListResponse,
		isLoading: getDevicesListLoading,
		isFetching: getDevicesListFetching
	} = useGetDevicesListQuery(filters);

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFiltersState) => ({
			...currentFiltersState,
			page: event.page + 1
		}));
	}

	function handleOpenDeviceConnectedCamerasDialog(deviceDetails: IDeviceRowDetails) {
		setSelectedCameraDetails(deviceDetails);
	}

	function handleCloseDeviceConnectedCamerasDialog() {
		setSelectedCameraDetails(null);
	}

	// RENDERER FUNCTIONS
	function renderStatusColumnCell(params: GridRenderCellParams<IDeviceRowDetails, "Online" | "Offline">) {
		return (
			<Box className={`devices-table-status-column-cell ${params.value}`}>
				<FiberManualRecordIcon color="inherit" fontSize="inherit" />

				<Typography variant="body2" textTransform="capitalize" color="var(--color-text-primary)">
					{params.value}
				</Typography>
			</Box>
		);
	}

	function renderImageQuotaColumnCell(
		params: GridRenderCellParams<IDeviceRowDetails, IDeviceRowDetails["image_quota"]>
	) {
		const quota = params.value;

		if (!quota) {
			return (
				<Box className="devices-table-image-quota-cell">
					<Typography variant="body2" color="var(--color-text-primary)" sx={{ opacity: 0.6 }}>
						—
					</Typography>
				</Box>
			);
		}

		const severity = getImageQuotaSeverity(quota.usage_percentage ?? 0, quota.is_quota_exceeded);
		const progressValue = Math.min(Math.max(quota.usage_percentage ?? 0, 0), 100);
		const periodLabel = quota.active_period_type === "weekly" ? "Weekly" : "Monthly";
		const cycleDateRange = formatQuotaRange(quota.period_start_ist, quota.period_end_ist);

		const tooltipContent = (
			<Box className="image-quota-tooltip-content">
				<Box className="image-quota-tooltip-header">
					<Typography variant="caption" className="image-quota-tooltip-chip">
						{periodLabel}
					</Typography>
					<Typography variant="caption" className="image-quota-tooltip-percentage">
						{`${(quota.usage_percentage ?? 0).toFixed(1)}% used`}
					</Typography>
				</Box>

				<Box className={`image-quota-tooltip-progress-card ${severity}`}>
					<LinearProgress
						variant="determinate"
						value={progressValue}
						classes={{ root: "image-quota-tooltip-progress", bar: "image-quota-tooltip-progress-bar" }}
					/>
					<Box className="image-quota-tooltip-progress-meta">
						<Typography variant="caption" className="image-quota-tooltip-progress-value">
							{`${quota.used.toLocaleString()} / ${(quota.limit ?? 0).toLocaleString()}`}
						</Typography>
						<Typography variant="caption" className="image-quota-tooltip-progress-remaining">
							{`${(quota.remaining ?? 0).toLocaleString()} remaining`}
						</Typography>
					</Box>
				</Box>

				<Box className="image-quota-tooltip-cycle-card">
					<Typography variant="caption" className="image-quota-tooltip-label">
						Cycle
					</Typography>
					<Typography variant="body2" className="image-quota-tooltip-cycle-key">
						{quota.current_period_key}
					</Typography>
					<Typography variant="caption" className="image-quota-tooltip-cycle-range">
						{cycleDateRange}
					</Typography>
				</Box>

				<Box className="image-quota-tooltip-row">
					<Typography variant="caption" className="image-quota-tooltip-label">
						Last reported at
					</Typography>
					<Typography variant="caption" className="image-quota-tooltip-value strong">
						{formatQuotaDate(quota.last_reported_at)}
					</Typography>
				</Box>

				{quota.is_quota_exceeded ? (
					<Typography variant="caption" className="image-quota-tooltip-alert error">
						Quota exceeded for this {periodLabel.toLowerCase()} cycle.
					</Typography>
				) : null}

				{quota.is_stale ? (
					<Typography variant="caption" className="image-quota-tooltip-alert warning">
						Usage data is stale — awaiting a fresh report from the device.
					</Typography>
				) : null}
			</Box>
		);

		return (
			<Tooltip
				arrow
				placement="top"
				title={tooltipContent}
				classes={{ tooltip: "image-quota-tooltip", arrow: "image-quota-tooltip-arrow" }}
				slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -6] } }] } }}
			>
				<Box className={`devices-table-image-quota-cell ${severity}`}>
					<Typography variant="caption" className="image-quota-cell-usage">
						{`${quota.used.toLocaleString()} / ${(quota.limit ?? 0).toLocaleString()}`}
					</Typography>

					<Box className="image-quota-cell-meta">
						{quota.is_stale ? <WarningAmberIcon className="image-quota-cell-stale-icon" fontSize="inherit" /> : null}

						<Typography variant="caption" className="image-quota-cell-percentage">
							{`${(quota.usage_percentage ?? 0).toFixed(0)}%`}
						</Typography>
					</Box>
				</Box>
			</Tooltip>
		);
	}

	function renderGlobalIPColumnCell(params: GridRenderCellParams<IDeviceRowDetails, string>) {
		return (
			<Tooltip
				title={params.value}
				enterDelay={2000}
				placement="bottom"
				slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -14] } }] } }}
			>
				<Box className="devices-table-global-ip-column-cell">
					<Typography variant="body2" overflow="hidden" textOverflow="ellipsis" flexGrow={1}>
						{params.value}
					</Typography>

					<IconButton size="small" onClick={() => handleOpenDeviceConnectedCamerasDialog(params.row)}>
						<OpenInNewIcon fontSize="inherit" sx={{ color: "var(--color-info-dark)" }} />
					</IconButton>
				</Box>
			</Tooltip>
		);
	}

	const columnsData: GridColDef[] = [
		{
			field: "status",
			headerName: "Status",
			minWidth: 120,
			flex: 1,
			sortable: false,
			renderCell: renderStatusColumnCell
		},
		{ field: "name", headerName: "Device Name", minWidth: 200, flex: 2, sortable: false },
		{ field: "serial_number", headerName: "Serial Number", minWidth: 300, flex: 1, sortable: false },
		{ field: "system_version", headerName: "System Version", minWidth: 150, flex: 1, sortable: false },
		{ field: "local_ip", headerName: "Local IP", minWidth: 150, flex: 1, sortable: false },
		{
			field: "global_ip",
			headerName: "Global IP",
			minWidth: 165,
			flex: 1,
			sortable: false,
			renderCell: renderGlobalIPColumnCell
		},
		{
			field: "image_quota",
			headerName: "Image Quota",
			minWidth: 150,
			flex: 1,
			sortable: false,
			renderCell: renderImageQuotaColumnCell
		}
	];

	function getDevicesRowsDetails(devicesList: IDeviceDetails[]): IDeviceRowDetails[] {
		return devicesList.map((deviceItem) => ({
			status: deviceItem.device_status ?? "Offline",
			id: deviceItem.id,
			name: deviceItem.device_name,
			serial_number: deviceItem.device_uuid,
			system_version: deviceItem.release_version,
			local_ip: deviceItem.local_ipv4_address,
			global_ip: deviceItem.global_ipv4_address,
			image_quota: deviceItem.image_quota ?? null
		}));
	}

	return (
		<Box className="device-camera-settings-screen-wrapper">
			<Box className="device-camera-settings-screen-header">
				<Typography variant="h6">Devices Connected</Typography>
			</Box>

			<Box className="devices-table-wrapper">
				<DataGrid
					columns={columnsData}
					rows={getDevicesRowsDetails(getDevicesListResponse?.results ?? [])}
					disableRowSelectionOnClick
					disableColumnMenu
					rowSpacingType="border"
					density="compact"
					columnHeaderHeight={60}
					pageSizeOptions={[PAGINATION_CONSTANTS.ROWS_PER_PAGE]}
					onPaginationModelChange={handlePageChange}
					paginationModel={{ page: filters.page ? filters.page - 1 : 0, pageSize: PAGINATION_CONSTANTS.ROWS_PER_PAGE }}
					hideFooter={(getDevicesListResponse?.count ?? 0) <= PAGINATION_CONSTANTS.ROWS_PER_PAGE}
					loading={getDevicesListLoading || getDevicesListFetching}
					classes={{
						columnSeparator: "devices-table-column-separator",
						columnHeader: "devices-table-column-header"
					}}
					slots={{ pagination: DevicesListPagination }}
					slotProps={{ footer: { sx: { justifyContent: "center" } } }}
				/>
			</Box>

			<ConnectedCamerasDialog
				open={!!selectedDeviceDetails}
				deviceDetails={selectedDeviceDetails}
				onClose={handleCloseDeviceConnectedCamerasDialog}
			/>
		</Box>
	);
};

export default DeviceCameraSettings;
